import { twMerge } from "tailwind-merge";

import type { FileItem } from "@/features/my-files/types";

import type { DesignFile } from "./types";

import { DesignThumbnail } from "./DesignThumbnail";
import { FileIcon } from "./FileIcon";
import { selectionOrder } from "./selectionOrder";
import { SelectionOrderBadge } from "./SelectionOrderBadge";

export function DesignsGridView({
  files,
  onOpen,
  selectedFileIds,
}: {
  files: FileItem[];
  onOpen: (fileItem: FileItem) => void;
  selectedFileIds: FileItem["id"][];
}) {
  const folders = files.filter((fileItem) => fileItem.type === "folder");
  const assets = files.filter(
    (fileItem): fileItem is DesignFile => fileItem.type === "file",
  );

  return (
    <div className="flex flex-col gap-md">
      {folders.length > 0 && (
        <>
          <GroupTitle>Folders</GroupTitle>
          <div className="grid grid-cols-1 gap-md md:grid-cols-3">
            {folders.map((folder) => (
              <button
                className="flex items-center gap-lg rounded-sm bg-bg-secondary px-xl py-lg text-left hover:bg-bg-secondary-hover"
                key={folder.id}
                onClick={() => onOpen(folder)}
                type="button"
              >
                <FileIcon fileItem={folder} />
                <span className="truncate text-sm text-text-primary">
                  {folder.name}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
      {assets.length > 0 && (
        <>
          <GroupTitle>Assets</GroupTitle>
          <div className="grid auto-rows-[180px] grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-md">
            {assets.map((asset) => (
              <AssetCard
                asset={asset}
                key={asset.id}
                onOpen={() => onOpen(asset)}
                selectionOrder={selectionOrder(selectedFileIds, asset.id)}
                showOrder={selectedFileIds.length > 1}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function GroupTitle({ children }: { children: string }) {
  return (
    <h3 className="px-xl py-lg text-xs font-medium text-text-tertiary">
      {children}
    </h3>
  );
}

function AssetCard({
  asset,
  onOpen,
  selectionOrder,
  showOrder,
}: {
  asset: DesignFile;
  onOpen: () => void;
  selectionOrder?: number;
  showOrder: boolean;
}) {
  const isSelected = selectionOrder !== undefined;

  return (
    <button
      className={twMerge(
        "relative flex flex-col items-center justify-center gap-md rounded-sm border border-transparent px-md pb-lg pt-md hover:bg-bg-secondary-hover",
        isSelected && "border-border-brand bg-bg-brand-primary",
      )}
      onClick={onOpen}
      type="button"
    >
      {selectionOrder !== undefined && showOrder && (
        <SelectionOrderBadge
          className="absolute left-sm top-sm"
          order={selectionOrder}
        />
      )}
      <span className="flex min-h-0 w-full flex-1 items-center justify-center">
        <DesignThumbnail
          className="max-h-full max-w-full"
          design={asset}
          iconClassName="size-[60px] shrink-0"
        />
      </span>
      <span className="w-full truncate text-center text-xs text-text-primary">
        {asset.name}
      </span>
    </button>
  );
}
